import {
  CameraIcon,
  CircleMinusIcon,
  ClipboardCheckIcon,
  MoveIcon,
  PackageCheckIcon,
  PrinterIcon,
  SplineIcon,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Status } from '@/schema/jawsScan';

type StatusDetail = { label: string; icon: LucideIcon; className: string };

export const STATUS_DETAILS: Record<Exclude<Status, null>, StatusDetail> = {
  [Status.scanned]: {
    label: 'Scanned',
    icon: CameraIcon,
    className: 'border-blue-500/40 bg-blue-500/15 text-blue-200',
  },
  [Status.segmented]: {
    label: 'Segmented',
    icon: SplineIcon,
    className: 'border-purple-500/40 bg-purple-500/15 text-purple-200',
  },
  [Status.corrected]: {
    label: 'Corrected',
    icon: MoveIcon,
    className: 'border-amber-500/40 bg-amber-500/15 text-amber-200',
  },
  [Status.printing]: {
    label: 'Printing',
    icon: PrinterIcon,
    className: 'border-orange-500/40 bg-orange-500/15 text-orange-200',
  },
  [Status.braces_created]: {
    label: 'Braces Created',
    icon: ClipboardCheckIcon,
    className: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-200',
  },
  [Status.printed]: {
    label: 'Printed',
    icon: PackageCheckIcon,
    className: 'border-teal-500/40 bg-teal-500/15 text-teal-200',
  },
  [Status.empty]: {
    label: 'Not started',
    icon: CircleMinusIcon,
    className: 'border-content-highlight/40 bg-content-dark/70 text-white/70',
  },
};

/**
 * Falls back rather than indexing blind: the server owns the stage vocabulary, and a
 * value this build has never heard of must not take the whole board down with it.
 */
export function statusDetail(status: Status): StatusDetail {
  return STATUS_DETAILS[status as Exclude<Status, null>] ?? STATUS_DETAILS[Status.empty];
}

export function StatusBadge({ status, className }: { status: Status; className?: string }) {
  const detail = statusDetail(status);
  const Icon = detail.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em]',
        detail.className,
        className,
      )}
    >
      <Icon className="h-4 w-4" />
      {detail.label}
    </span>
  );
}
